import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Backdrop,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  Favorite,
  FitnessCenter,
  CheckCircle,
  CloudOff,
  Refresh,
  ExpandMore,
  SkipNext,
  PlayArrow,
  Timer,
  Visibility,
  Close,
} from '@mui/icons-material';
import { useWorkout } from '../hooks/useWorkout';
import { WorkoutUtils } from '../utils/workoutUtils';
import { Hero, HeroMetric } from './Hero';
import { WorkoutItem } from './WorkoutItem';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [quickActionsExpanded, setQuickActionsExpanded] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [exercisePreviewOpen, setExercisePreviewOpen] = useState(false);
  
  const { 
    userProgress, 
    currentWorkoutState,
    getCurrentWeekExercises,
    isExerciseCompleted,
    startWorkout,
    finishCurrentWorkout,
    progressToNextDay,
    skipCurrentDay,
    getWorkoutStats,
    addRandomWeek1And2Stats,
    getElapsedTime,
    isWorkoutStarted,
    canCompleteWorkout,
    isLoading, 
    error,
    isOnline
  } = useWorkout();

  // Update elapsed time every minute
  React.useEffect(() => {
    if (isWorkoutStarted()) {
      const interval = setInterval(() => {
        setElapsedTime(getElapsedTime());
      }, 60000); // Update every minute

      // Update immediately
      setElapsedTime(getElapsedTime());

      return () => clearInterval(interval);
    } else {
      setElapsedTime(0);
    }
  }, [isWorkoutStarted, getElapsedTime]);

  const stats = getWorkoutStats();
  const currentExercises = getCurrentWeekExercises();

  // Calculate progress using WorkoutUtils
  const currentWeek = userProgress?.currentWeek || 1;
  const todaysProgress = WorkoutUtils.calculateDailyProgress(currentExercises, isExerciseCompleted);
  const weekProgress = WorkoutUtils.calculateWeekProgress(userProgress, currentWeek, todaysProgress);

  // Check if all exercises are completed and workout can be finished
  const allExercisesCompleted = Array.isArray(currentExercises) && 
    currentExercises.length > 0 && 
    todaysProgress.completed === todaysProgress.total &&
    currentWorkoutState; // Only show if there's an active workout

  const handleStartWorkout = async () => {
    await startWorkout();
  };

  const handleFinishWorkout = async () => {
    await finishCurrentWorkout();
  };

  const handleProgressToNextDay = async () => {
    await progressToNextDay();
  };

  const handleSkipDay = async () => {
    setSkipDialogOpen(true);
  };

  const handleConfirmSkip = async () => {
    setIsSkipping(true);
    try {
      await skipCurrentDay();
      setSkipDialogOpen(false);
    } catch (err) {
      // Handle error if needed
    } finally {
      setIsSkipping(false);
    }
  };

  const handleAddRandomStats = async () => {
    await addRandomWeek1And2Stats();
  };

  // Exercise preview handlers
  const handleViewExercises = () => {
    setExercisePreviewOpen(true);
  };

  const handleCloseExercisePreview = () => {
    setExercisePreviewOpen(false);
  };

  if (isLoading) {
    return (
      <Backdrop 
        open 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: 'background.default',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">Loading your workout data...</Typography>
        </Stack>
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ pt: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Error Loading Data</Typography>
          <Typography>{error}</Typography>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outlined" 
            sx={{ mt: 2 }}
            startIcon={<Refresh />}
          >
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  const currentDay = userProgress?.currentDay || 'Monday';
  const isRestDay = WorkoutUtils.isRestDay(currentDay);
  const nextDay = WorkoutUtils.getNextDay(currentDay);
  const isLastRestDay = currentDay === 'Sunday'; // Sunday is the last day before new week
  
  // Check if current week is a ramp up week
  const isRampUpWeek = [1, 2, 6, 7].includes(currentWeek);

  // Prepare Hero metrics
  const heroMetrics: HeroMetric[] = isRestDay ? [
    {
      value: isLastRestDay ? 'Finished!' : 'Rest Day',
      label: isLastRestDay ? 'Ready for next week' : 'Recovery time',
      size: 'large',
      align: 'center'
    }
  ] : [
    {
      value: `${todaysProgress.completed}/${todaysProgress.total}`,
      label: "Today's Exercises",
      size: 'medium',
      align: 'center'
    },
    {
      value: `${weekProgress}%`,
      label: 'Weekly Progress',
      size: 'medium',
      align: 'right'
    }
  ];

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Offline indicator */}
      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<CloudOff />}>
          <Typography variant="body2">
            You're offline. Changes will sync when you reconnect.
          </Typography>
        </Alert>
      )}

      {/* Hero Section */}
      <Hero
        title={`Week ${currentWeek} Data`}
        subtitle={`${WorkoutUtils.getWorkoutType(currentDay)} • ${currentDay}`}
        subtitle2={isRampUpWeek ? 'Ramp Up Week' : undefined}
        metrics={heroMetrics}
        progressValue={weekProgress}
        progressLabel="Weekly Progress"
      />
      
      {/* Start/Complete Workout Button */}
      {!isRestDay && Array.isArray(currentExercises) && currentExercises.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {!isWorkoutStarted() ? (
            <Card sx={{ textAlign: 'center', py: 8 }}>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <FitnessCenter sx={{ fontSize: 80, color: 'primary.main' }} />
                </Box>
                <Typography 
                  variant="h4" 
                  component="h3" 
                  fontWeight="bold" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    hyphens: 'auto'
                  }}
                >
                  Ready to Start?
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                  {currentExercises.length} exercises waiting for you today
                </Typography>
                <Button
                  onClick={handleStartWorkout}
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    }
                  }}
                >
                  Start Workout
                </Button>
                <Button
                  onClick={handleViewExercises}
                  variant="outlined"
                  size="large"
                  startIcon={<Visibility />}
                  sx={{
                    mt: 2,
                    fontWeight: 'bold',
                    py: 1.5,
                    px: 4,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    }
                  }}
                >
                  View Exercises
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ 
              background: canCompleteWorkout() 
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                {canCompleteWorkout() ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      🎉 All Exercises Completed!
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                      Workout Duration: {elapsedTime} minutes
                    </Typography>
                    <Button
                      onClick={handleFinishWorkout}
                      variant="contained"
                      size="large"
                      startIcon={<CheckCircle />}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontWeight: 'bold',
                        py: 1.5,
                        px: 4,
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                        }
                      }}
                    >
                      Complete Workout
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Timer /> Workout in Progress
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Elapsed Time: {elapsedTime} minutes
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      )}
      
      {/* Exercise List - Only show when workout is started */}
      {!isRestDay && isWorkoutStarted() && Array.isArray(currentExercises) && currentExercises.length > 0 && (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {currentExercises.map((exercise, index) => (
            <WorkoutItem
              key={index}
              exercise={exercise}
              index={index}
              isCompleted={isExerciseCompleted(index)}
              onClick={() => navigate(`/exercise/${index}`)}
            />
          ))}
        </Stack>
      )}
      
      {/* Rest Day or No Exercises */}
      {isRestDay ? (
        <Stack spacing={2}>
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Favorite sx={{ fontSize: 80, color: 'success.main' }} />
              </Box>
              <Typography 
                variant="h4" 
                component="h3" 
                fontWeight="bold" 
                gutterBottom
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {isLastRestDay ? '🎉 Finished!' : '😴 Rest Day'}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                {isLastRestDay 
                  ? 'Great job this week! Ready to start the next week?'
                  : 'Take a well-deserved break today!'
                }
              </Typography>
              <Button
                onClick={handleProgressToNextDay}
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  py: 1.5,
                  px: 4,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  }
                }}
              >
                {isLastRestDay 
                  ? `Start Week ${currentWeek + 1}` 
                  : `Continue to ${nextDay}`
                }
              </Button>
            </CardContent>
          </Card>
        </Stack>
      ) : !Array.isArray(currentExercises) || currentExercises.length === 0 ? (
        <Stack spacing={2}>
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <FitnessCenter sx={{ fontSize: 80, color: 'text.secondary' }} />
              </Box>
              <Typography 
                variant="h4" 
                component="h3" 
                fontWeight="bold" 
                gutterBottom
                sx={{
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}
              >
                No Exercises Found
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Unable to load workout exercises.
              </Typography>
            </CardContent>
          </Card>
        </Stack>
      ) : null}

      {/* Skip Day Section */}
      {!isRestDay && (
        <Box sx={{ mt: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" gutterBottom>
                Can't complete today's workout?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Skip to the next day and continue your fitness journey tomorrow.
              </Typography>
              <Button
                onClick={handleSkipDay}
                variant="outlined"
                color="warning"
                startIcon={<SkipNext />}
                sx={{
                  fontWeight: 'bold',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  }
                }}
              >
                Skip Today's Workout
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Status Info */}
      <Box sx={{ mt: 4 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Status
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip 
                label={isOnline ? 'Online' : 'Offline'} 
                color={isOnline ? 'success' : 'warning'} 
                size="small" 
              />
              <Chip 
                label={`Week ${currentWeek}`} 
                color="primary" 
                size="small" 
              />
              <Chip 
                label={currentDay} 
                color="secondary" 
                size="small" 
              />
              <Chip 
                label={`${stats.totalWorkouts} workouts`} 
                variant="outlined" 
                size="small" 
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Quick Actions Dropdown */}
      <Box sx={{ mt: 2 }}>
        <Accordion 
          expanded={quickActionsExpanded} 
          onChange={(event, isExpanded) => setQuickActionsExpanded(isExpanded)}
          sx={{ 
            borderRadius: '24px !important',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { borderRadius: '24px !important' },
            '& .MuiAccordionSummary-root': { 
              borderRadius: '24px !important',
              '&.Mui-expanded': { borderRadius: '24px 24px 0 0 !important' }
            },
            '& .MuiAccordionDetails-root': { 
              borderRadius: '0 0 24px 24px !important' 
            },
            overflow: 'hidden'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
          >
            <Typography variant="h6" color="text.secondary">
              Developer Actions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                onClick={handleAddRandomStats}
                variant="outlined"
                size="small"
                color="secondary"
              >
                Add Sample Data
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Skip Day Confirmation Dialog */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" component="h2" fontWeight="bold" color="warning.main">
            Skip Today's Workout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to skip today's workout?
          </Typography>
          <Typography variant="body1">
            This will mark today as skipped and move you to the next workout day. You can always come back to review skipped workouts in your weekly history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setSkipDialogOpen(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSkip}
            variant="contained"
            color="warning"
            disabled={isSkipping}
            startIcon={isSkipping ? <CircularProgress size={16} /> : <SkipNext />}
            sx={{
              fontWeight: 'bold',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              }
            }}
          >
            {isSkipping ? 'Skipping...' : 'Skip Workout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exercise Preview Dialog */}
      <Dialog
        open={exercisePreviewOpen}
        onClose={handleCloseExercisePreview}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" component="h2" fontWeight="bold">
              Today's Exercises
            </Typography>
            <IconButton
              onClick={handleCloseExercisePreview}
              sx={{ color: 'text.secondary' }}
            >
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {Array.isArray(currentExercises) ? currentExercises.length : 0} exercises • {currentDay}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ py: 0 }}>
            {Array.isArray(currentExercises) && currentExercises.map((exercise, index) => {
              // Calculate warmup and working sets similar to WorkoutItem
              const warmupSets = exercise.warmup_sets === '0' ? 0 : parseInt(exercise.warmup_sets.split('-').pop() || exercise.warmup_sets);
              const workingSets = parseInt(exercise.working_sets.split('-').pop() || exercise.working_sets);
              
              return (
                <ListItem
                  key={index}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 2.5,
                    px: 3,
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 56 }}>
                    <Chip
                      icon={<FitnessCenter />}
                      label=""
                      sx={{ 
                        width: 48, 
                        height: 48,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        '& .MuiChip-label': { display: 'none' },
                        '& .MuiChip-icon': { fontSize: 24, margin: 0, color: 'white' }
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {exercise.exercise}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {warmupSets} warmup • {workingSets} working
                        {exercise.reps && ` • ${exercise.reps} reps`}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseExercisePreview}
            variant="outlined"
            fullWidth
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HomePage; 